import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  assertAdmin,
  randomToken,
  slugSchema,
  tokenSchema,
  type PortalClient,
  type PortalProject,
  type PortalProposal,
} from "@/lib/portal-shared";

export const getClientPortal = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z.object({ client: slugSchema, token: tokenSchema }).parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: client } = await supabaseAdmin
      .from("portal_clients")
      .select("id, name, slug, notes, active, access_token")
      .eq("slug", data.client)
      .maybeSingle();

    if (!client || !client.active || client.access_token !== data.token) {
      return { ok: false as const };
    }

    const { data: projects } = await supabaseAdmin
      .from("portal_projects")
      .select("id, client_id, name, slug, description, sort_order")
      .eq("client_id", client.id)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });

    return {
      ok: true as const,
      client: { name: client.name, slug: client.slug, notes: client.notes },
      projects: (projects ?? []) as PortalProject[],
    };
  });

export const getProjectPortal = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z.object({ client: slugSchema, project: slugSchema, token: tokenSchema }).parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: client } = await supabaseAdmin
      .from("portal_clients")
      .select("id, name, slug, active, access_token")
      .eq("slug", data.client)
      .maybeSingle();

    if (!client || !client.active || client.access_token !== data.token) {
      return { ok: false as const };
    }

    const { data: project } = await supabaseAdmin
      .from("portal_projects")
      .select("id, name, slug, description")
      .eq("client_id", client.id)
      .eq("slug", data.project)
      .maybeSingle();

    if (!project) return { ok: false as const };

    const { data: proposals } = await supabaseAdmin
      .from("portal_proposals")
      .select(
        "id, project_id, title, slug, kind, storage_path, content_type, file_size, sort_order, created_at",
      )
      .eq("project_id", project.id)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });

    return {
      ok: true as const,
      client: { name: client.name, slug: client.slug },
      project: {
        name: project.name,
        slug: project.slug,
        description: project.description,
      },
      proposals: (proposals ?? []) as PortalProposal[],
    };
  });

export const ensureAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const email = String((context.claims as { email?: string }).email ?? "")
      .trim()
      .toLowerCase();
    if (!email) return { isAdmin: false, email: "" };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: allowed } = await supabaseAdmin
      .from("admin_emails")
      .select("email")
      .eq("email", email)
      .maybeSingle();

    if (!allowed) return { isAdmin: false, email };

    await supabaseAdmin
      .from("user_roles")
      .upsert({ user_id: context.userId, role: "admin" }, { onConflict: "user_id,role" });

    return { isAdmin: true, email };
  });

export const listPortalAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as never);
    const { supabase } = context;
    const [{ data: clients }, { data: projects }, { data: proposals }] = await Promise.all([
      supabase.from("portal_clients").select("*").order("created_at", { ascending: false }),
      supabase.from("portal_projects").select("*").order("sort_order", { ascending: true }),
      supabase.from("portal_proposals").select("*").order("sort_order", { ascending: true }),
    ]);
    return {
      clients: (clients ?? []) as PortalClient[],
      projects: (projects ?? []) as PortalProject[],
      proposals: (proposals ?? []) as PortalProposal[],
    };
  });

export const createPortalClient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        name: z.string().trim().min(2).max(120),
        slug: slugSchema,
        notes: z.string().trim().max(1000).optional().default(""),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { data: row, error } = await context.supabase
      .from("portal_clients")
      .insert({
        name: data.name,
        slug: data.slug,
        notes: data.notes || null,
        access_token: randomToken(),
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return row as PortalClient;
  });

export const updatePortalClient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        id: z.string().uuid(),
        name: z.string().trim().min(2).max(120),
        notes: z.string().trim().max(1000).optional().default(""),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase
      .from("portal_clients")
      .update({ name: data.name, notes: data.notes || null })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const updatePortalProject = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        id: z.string().uuid(),
        name: z.string().trim().min(2).max(120),
        description: z.string().trim().max(1000).optional().default(""),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase
      .from("portal_projects")
      .update({ name: data.name, description: data.description || null })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const updatePortalProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ id: z.string().uuid(), title: z.string().trim().min(2).max(160) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase
      .from("portal_proposals")
      .update({ title: data.title })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderPortalProposals = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ ids: z.array(z.string().uuid()).min(1).max(200) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    for (let i = 0; i < data.ids.length; i++) {
      const { error } = await context.supabase
        .from("portal_proposals")
        .update({ sort_order: i })
        .eq("id", data.ids[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });


export const rotatePortalToken = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const token = randomToken();
    const { error } = await context.supabase
      .from("portal_clients")
      .update({ access_token: token })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true, token };
  });

export const deletePortalClient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("portal_clients").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const createPortalProject = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        clientId: z.string().uuid(),
        name: z.string().trim().min(2).max(120),
        slug: slugSchema,
        description: z.string().trim().max(1000).optional().default(""),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { data: row, error } = await context.supabase
      .from("portal_projects")
      .insert({
        client_id: data.clientId,
        name: data.name,
        slug: data.slug,
        description: data.description || null,
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return row as PortalProject;
  });

export const deletePortalProject = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { error } = await context.supabase.from("portal_projects").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const uploadPortalProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        projectId: z.string().uuid(),
        title: z.string().trim().min(2).max(160),
        slug: slugSchema,
        fileName: z.string().trim().min(1).max(200),
        contentType: z.string().trim().min(3).max(120),
        fileBase64: z.string().min(1).max(12_000_000),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);

    const binary = Uint8Array.from(atob(data.fileBase64), (c) => c.charCodeAt(0));
    if (binary.byteLength > 8 * 1024 * 1024) throw new Error("File is larger than 8 MB");

    const isHtml = data.contentType.includes("html") || /\.html?$/i.test(data.fileName);
    const ext = data.fileName.includes(".")
      ? data.fileName.split(".").pop()!.toLowerCase().replace(/[^a-z0-9]/g, "")
      : "bin";
    const path = `${data.projectId}/${data.slug}-${Date.now()}.${ext || "bin"}`;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error: upErr } = await supabaseAdmin.storage
      .from("client-portal")
      .upload(path, binary, { contentType: data.contentType, upsert: true });
    if (upErr) throw new Error(upErr.message);

    const { data: row, error } = await context.supabase
      .from("portal_proposals")
      .insert({
        project_id: data.projectId,
        title: data.title,
        slug: data.slug,
        kind: isHtml ? "html" : "file",
        storage_path: path,
        content_type: data.contentType,
        file_size: binary.byteLength,
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return row as PortalProposal;
  });

export const deletePortalProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const { data: row } = await context.supabase
      .from("portal_proposals")
      .select("storage_path")
      .eq("id", data.id)
      .maybeSingle();
    const { error } = await context.supabase.from("portal_proposals").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    if (row?.storage_path) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.storage.from("client-portal").remove([row.storage_path]);
    }
    return { ok: true };
  });
